import {Modal, ModalContent, ModalTitle} from '@hm/fundamental'

import type {ImportPlantOption} from '../../helpers'
import type {ParseError} from '../../helpers/excelParser'
import type {ImportStep} from '../../hooks'
import {useRiskSurveyTranslation} from '../../hooks'
import type {
  CreateRiaRequest as ImportPreviewRow,
  ImportDuplicateCheckResponseDto as ImportDuplicateCheckResult
} from '../../types'

import {ImportModalFooter} from './ImportModalFooter'
import {PreviewStep} from './PreviewStep'
import {SubmittingStep} from './SubmittingStep'
import {UploadStep} from './UploadStep'

type ImportReportModalProps = {
  open: boolean
  onClose: () => void
  step: ImportStep
  onFileSelected: (file: File) => void
  isLoading: boolean
  parsedRows: ImportPreviewRow[]
  parseErrors: ParseError[]
  excludedRows: Set<number>
  onToggleRow: (index: number) => void
  onBack: () => void
  onImport: () => void
  isSubmitting: boolean
  duplicateRiNumbers: string[]
  importResultStatus?: 'success' | 'error' | null
  importResultMessage?: string | null
  onImportResultBackToPreview?: () => void
  needsPlantInput?: boolean
  plantId?: string
  siteName?: string
  plantOptions?: ImportPlantOption[]
  onPlantIdChange?: (value: string) => void
  duplicateImportCheck?: ImportDuplicateCheckResult | null
  isCheckingImportDuplicate?: boolean
}

const getTitleKey = (step: ImportStep): string => {
  switch (step) {
    case 'upload':
      return 'import.titleUpload'
    case 'preview':
      return 'import.titlePreview'
    default:
      return 'import.titleImporting'
  }
}

export const ImportReportModal = ({
  open,
  onClose,
  step,
  onFileSelected,
  isLoading,
  parsedRows,
  parseErrors,
  excludedRows,
  onToggleRow,
  onBack,
  onImport,
  isSubmitting,
  duplicateRiNumbers,
  importResultStatus,
  importResultMessage,
  onImportResultBackToPreview,
  needsPlantInput,
  plantId,
  siteName,
  plantOptions,
  onPlantIdChange,
  duplicateImportCheck,
  isCheckingImportDuplicate
}: ImportReportModalProps) => {
  const {t} = useRiskSurveyTranslation()

  const validCount = parsedRows.length - excludedRows.size
  const plantInputValid = !needsPlantInput || !!(plantId?.trim() && siteName?.trim())

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={step === 'preview' ? 'wide' : 'thin'}
      data-cy="import-modal"
    >
      <ModalTitle>{t(getTitleKey(step))}</ModalTitle>
      <ModalContent>
        {step === 'upload' && (
          <UploadStep
            needsPlantInput={needsPlantInput}
            plantId={plantId}
            siteName={siteName}
            plantOptions={plantOptions}
            onPlantIdChange={onPlantIdChange}
            onFileSelected={onFileSelected}
            isLoading={isLoading}
            plantInputValid={plantInputValid}
          />
        )}
        {step === 'preview' && (
          <PreviewStep
            duplicateRiNumbers={duplicateRiNumbers}
            duplicateImportCheck={duplicateImportCheck}
            parsedRows={parsedRows}
            parseErrors={parseErrors}
            excludedRows={excludedRows}
            onToggleRow={onToggleRow}
          />
        )}
        {step === 'submitting' && (
          <SubmittingStep
            importResultStatus={importResultStatus}
            importResultMessage={importResultMessage}
          />
        )}
      </ModalContent>
      <ImportModalFooter
        step={step}
        onClose={onClose}
        onBack={onBack}
        onImport={onImport}
        isSubmitting={isSubmitting}
        duplicateRiNumbers={duplicateRiNumbers}
        isCheckingImportDuplicate={isCheckingImportDuplicate}
        duplicateImportCheck={duplicateImportCheck}
        plantInputValid={plantInputValid}
        validCount={validCount}
        importResultStatus={importResultStatus}
        onImportResultBackToPreview={onImportResultBackToPreview}
      />
    </Modal>
  )
}
